import { StandardEvents } from '@shopify/events';

class SellingPlanSelector extends HTMLElement {
  #abortController = new AbortController();
  #scope;

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    this.#scope = this.closest('.shopify-section, dialog, product-card');
    const { signal } = this.#abortController;

    this.addEventListener('change', this.#handleChange, { signal });
    this.#scope?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    requestAnimationFrame(() => this.#applySelection(false));
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#restorePrices();
  }

  #handleChange = (event) => {
    if (!(event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement)) return;
    this.#applySelection(true);
  };

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const wasSubscription = this.#isSubscriptionSelected();
    const selectedPlanId = this.#planSelect?.value;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        if (detail.productId && detail.productId !== this.dataset.productId) return;

        const selectorId = CSS.escape(this.dataset.selectorId || '');
        const newSelector = detail.html.querySelector(`selling-plan-selector[data-selector-id="${selectorId}"]`);

        if (!newSelector || !newSelector.innerHTML.trim()) {
          this.#restorePrices();
          this.replaceChildren();
          this.hidden = true;
          return;
        }

        this.innerHTML = newSelector.innerHTML;
        this.dataset.productId = newSelector.dataset.productId || this.dataset.productId;
        this.hidden = newSelector.hidden;

        const subscriptionRadio = this.querySelector('input[value="subscription"]');
        const oneTimeRadio = this.querySelector('input[value="one-time"]');
        const planSelect = this.#planSelect;

        if (wasSubscription && subscriptionRadio instanceof HTMLInputElement && planSelect) {
          subscriptionRadio.checked = true;
          if (oneTimeRadio instanceof HTMLInputElement) oneTimeRadio.checked = false;

          if (selectedPlanId && [...planSelect.options].some((option) => option.value === selectedPlanId)) {
            planSelect.value = selectedPlanId;
          }
        }

        this.#applySelection(false);
        requestAnimationFrame(() => {
          if (this.isConnected) this.#syncDisplayedPrices();
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[selling-plan-selector] Event promise rejected:', error);
      });
  };

  get #planSelect() {
    return this.querySelector('[data-selling-plan-select]');
  }

  get #planInput() {
    return this.querySelector('[data-selling-plan-input]');
  }

  #isSubscriptionSelected() {
    const radio = this.querySelector('input[value="subscription"]');
    return radio instanceof HTMLInputElement && radio.checked;
  }

  #applySelection(updateUrl) {
    const isSubscription = this.#isSubscriptionSelected();
    const planSelect = this.#planSelect;
    const planInput = this.#planInput;
    const selectedOption = planSelect?.selectedOptions[0];

    for (const option of this.querySelectorAll('.selling-plan-option')) {
      const radio = option.querySelector('.selling-plan-option__radio');
      option.classList.toggle('selling-plan-option--selected', radio instanceof HTMLInputElement && radio.checked);
    }

    if (planSelect) planSelect.disabled = !isSubscription;
    if (planInput) {
      planInput.disabled = !isSubscription;
      planInput.value = selectedOption?.value || '';
    }

    this.#updateSubscriptionSummary(selectedOption);

    this.#syncDisplayedPrices();

    if (updateUrl && this.dataset.updateUrl === 'true') this.#updateUrl(isSubscription ? selectedOption?.value : null);

    this.dispatchEvent(
      new CustomEvent('selling-plan:change', {
        bubbles: true,
        detail: { sellingPlanId: isSubscription ? selectedOption?.value || null : null },
      })
    );
  }

  #updateSubscriptionSummary(option) {
    if (!option) return;
    const price = this.querySelector('[data-subscription-price]');
    const comparePrice = this.querySelector('[data-subscription-compare-price]');
    const comparePriceValue = option.dataset.comparePrice || '';
    const hasDiscount = Number(option.dataset.discount || 0) > 0;

    if (price) price.textContent = option.dataset.price || '';
    if (comparePrice) {
      comparePrice.textContent = comparePriceValue;
      comparePrice.hidden = !hasDiscount;
    }
  }

  #syncDisplayedPrices() {
    const selectedOption = this.#planSelect?.selectedOptions[0];
    if (this.#isSubscriptionSelected() && selectedOption) {
      this.#showSubscriptionPrice(selectedOption);
    } else {
      this.#restorePrices();
    }
  }

  #getPriceContainers() {
    if (!this.#scope) return [];
    const productId = this.dataset.productId;
    const productPrices = [
      ...this.#scope.querySelectorAll(`product-price[data-product-id="${CSS.escape(productId || '')}"] [ref="priceContainer"]`),
    ];
    const stickyPrice = this.#scope.querySelector('sticky-add-to-cart .sticky-add-to-cart__price [ref="priceContainer"]');
    if (stickyPrice) productPrices.push(stickyPrice);
    return productPrices;
  }

  #showSubscriptionPrice(option) {
    const priceText = option.dataset.price || '';
    const compareText = option.dataset.comparePrice || '';
    const hasDiscount = Number(option.dataset.discount || 0) > 0;

    for (const container of this.#getPriceContainers()) {
      if (!container.dataset.oneTimePriceHtml) container.dataset.oneTimePriceHtml = container.innerHTML;
      container.replaceChildren(this.#buildPriceMarkup(priceText, compareText, hasDiscount));
      container.dataset.sellingPlanPrice = 'true';
    }
  }

  #buildPriceMarkup(priceText, compareText, hasDiscount) {
    const wrapper = document.createElement('span');
    wrapper.className = 'selling-plan-price';

    const price = document.createElement('span');
    price.className = 'price-item__group price';
    const priceLabel = document.createElement('span');
    priceLabel.className = 'visually-hidden';
    priceLabel.textContent = 'Subscription price ';
    price.append(priceLabel, document.createTextNode(priceText));
    wrapper.append(price);

    if (hasDiscount && compareText) {
      const compare = document.createElement('s');
      compare.className = 'price-item__group price-item--regular compare-at-price';
      const compareLabel = document.createElement('span');
      compareLabel.className = 'visually-hidden';
      compareLabel.textContent = 'Regular price ';
      compare.append(compareLabel, document.createTextNode(compareText));
      wrapper.append(compare);
    }

    const suffix = document.createElement('span');
    suffix.className = 'selling-plan-price__suffix';
    suffix.textContent = ' / delivery';
    wrapper.append(suffix);
    return wrapper;
  }

  #restorePrices() {
    for (const container of this.#getPriceContainers()) {
      if (!container.dataset.oneTimePriceHtml) continue;
      container.innerHTML = container.dataset.oneTimePriceHtml;
      delete container.dataset.oneTimePriceHtml;
      delete container.dataset.sellingPlanPrice;
    }
  }

  #updateUrl(sellingPlanId) {
    const url = new URL(window.location.href);
    if (sellingPlanId) url.searchParams.set('selling_plan', sellingPlanId);
    else url.searchParams.delete('selling_plan');
    history.replaceState({}, '', url.toString());
  }
}

if (!customElements.get('selling-plan-selector')) {
  customElements.define('selling-plan-selector', SellingPlanSelector);
}
